/**
 * MonthYearPicker - Accessible dropdown for month/year selection
 * Replaces native <select> to ensure selected label is always visible
 * Uses React-controlled rendering instead of browser-native select
 */
import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import Popover from './Popover.jsx';

export default function MonthYearPicker({ 
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
}) {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  // Find the selected option to display its label
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayText = selectedOption ? selectedOption.label : '';

  // Close on outside click and escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange({ target: { value: String(optionValue) } });
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'ArrowUp' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
    } else if (isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const currentIndex = options.findIndex(opt => String(opt.value) === String(value));
      let newIndex = currentIndex;
      if (e.key === 'ArrowDown') {
        newIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }
      handleSelect(options[newIndex].value);
    }
  };

  return (
    <div style={{ 
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
    }}>
      <label 
        htmlFor={id}
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          whiteSpace: 'nowrap',
          marginRight: 'var(--spacing-2)',
          lineHeight: '1',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {label}:
      </label>
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        offset={4}
        trigger={({ ref, ...props }) => (
          <button
            {...props}
            ref={(node) => {
              buttonRef.current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            id={id}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              paddingRight: 'var(--spacing-6)', // Space for dropdown arrow
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)'}`,
              backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
              fontWeight: 'inherit',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              height: '36px',
              minWidth: '140px',
              textAlign: 'left',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              lineHeight: '1',
              boxSizing: 'border-box',
            }}
          >
            <span style={{ 
              flex: 1,
              lineHeight: '1',
              display: 'flex',
              alignItems: 'center',
            }}>{displayText || 'Select...'}</span>
            <span style={{ 
              marginLeft: 'var(--spacing-2)',
              fontSize: '0.75em',
              opacity: 0.7,
              lineHeight: '1',
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
            }}>▼</span>
          </button>
        )}
      >
        <div
          role="listbox"
          style={{
            backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
            border: `1px solid ${theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)'}`,
            borderRadius: 'var(--radius-sm)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '200px',
            overflowY: 'auto',
            minWidth: '140px',
            padding: '4px 0',
          }}
        >
          {options.map((option) => {
            const isSelected = String(option.value) === String(value);
            return (
              <div
                key={option.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(option.value);
                  }
                }}
                tabIndex={0}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  cursor: 'pointer',
                  backgroundColor: isSelected
                    ? (theme === 'dark' ? 'var(--color-bg-hover-dark)' : 'var(--color-bg-hover)')
                    : 'transparent',
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? 'var(--color-bg-hover-dark)' : 'var(--color-bg-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      </Popover>
    </div>
  );
}
